import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Star, Users } from "lucide-react";
import { api, errMsg, fmtMoney, imgSrc } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSeo } from "../lib/seo";
import { STATUSES } from "../lib/constants";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import PropertyEditor from "../components/admin/PropertyEditor";
import Records from "../components/admin/Records";
import Messages from "../components/Messages";
import { Field } from "../components/Common";

const Stat = ({ label, value, testid }) => (
  <div className="bg-white p-5" data-testid={testid}><p className="text-[10px] uppercase tracking-[0.14em] text-mist">{label}</p><p className="metric mt-2 text-3xl">{value}</p></div>
);

function Overview() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setA(r.data)); }, []);
  if (!a) return <p className="text-mist">Loading…</p>;
  const max = Math.max(1, ...Object.values(a.by_status));
  return (
    <div className="space-y-8" data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-navy/10 border border-navy/10">
        <Stat label="Properties" value={a.properties} testid="an-properties" />
        <Stat label="Investor Accounts" value={a.investors} testid="an-investors" />
        <Stat label="Criteria Submissions" value={a.criteria} testid="an-criteria" />
        <Stat label="New Leads" value={a.new_leads} testid="an-leads" />
        <Stat label="Pending Requests" value={a.requests_pending} testid="an-requests" />
        <Stat label="Seller Submissions" value={a.seller_submissions} testid="an-sellers" />
        <Stat label="Inquiries" value={a.inquiries} testid="an-inquiries" />
        <Stat label="Property Types" value={Object.keys(a.by_type).length} />
      </div>
      <div className="bg-white border border-navy/10 p-6">
        <p className="eyebrow mb-5">Deal Pipeline by Status</p>
        <div className="space-y-2.5">
          {Object.entries(a.by_status).map(([s, n]) => (
            <div key={s} className="flex items-center gap-4 text-sm"><span className="w-32 shrink-0 text-navy">{s}</span><div className="flex-1 bg-paper h-2"><div className="h-2 bg-navy" style={{ width: `${(n / max) * 100}%` }} /></div><span className="metric w-6 text-right">{n}</span></div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MatchesDialog({ p, onClose }) {
  const [rows, setRows] = useState(null);
  const [min, setMin] = useState(70);
  useEffect(() => { api.get(`/admin/properties/${p.id}/matches`).then((r) => setRows(r.data)); }, [p.id]);
  const notify = async () => {
    try { const { data } = await api.post(`/admin/properties/${p.id}/notify-matches`, { min_score: Number(min) }); toast.success(`Notified ${data.sent} investor(s)`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto rounded-none" data-testid="matches-dialog">
        <DialogHeader><DialogTitle className="font-serif text-3xl text-navy">Investor Matches · {p.title}</DialogTitle></DialogHeader>
        <div className="flex items-end gap-3">
          <Field label="Min score"><input className="field !py-2 w-24" value={min} onChange={(e) => setMin(e.target.value)} data-testid="matches-min-score" /></Field>
          <button onClick={notify} className="btn-gold !py-2.5" data-testid="matches-notify-btn">Notify Matching Investors</button>
        </div>
        <div className="divide-y divide-navy/10 border border-navy/10">
          {rows?.length === 0 && <p className="p-5 text-mist">No investor criteria yet.</p>}
          {rows?.map((m) => (
            <div key={m.criteria_id} className="flex items-center gap-4 p-4">
              <span className="metric w-14 text-lg text-gold">{m.score}%</span>
              <div className="flex-1"><p className="font-semibold text-navy">{m.name} {!m.user_id && <span className="text-[10px] text-mist">(no portal account)</span>}</p><p className="text-xs text-mist">{m.summary}</p></div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Properties() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [matches, setMatches] = useState(null);
  const load = () => api.get("/admin/properties").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const setStatus = async (p, status) => { await api.patch(`/admin/properties/${p.id}/status`, { status }); toast.success(`Status → ${status}`); load(); };
  const toggleFeatured = async (p) => { await api.put(`/admin/properties/${p.id}`, { ...p, featured: !p.featured }); load(); };
  return (
    <div className="space-y-4" data-testid="admin-properties">
      <button onClick={() => setEdit({})} className="btn-primary" data-testid="admin-add-property"><Plus className="h-4 w-4" /> Add Property</button>
      <div className="border border-navy/10 divide-y divide-navy/10 bg-white">
        {items.map((p) => (
          <div key={p.id} className="flex flex-col md:flex-row md:items-center gap-4 p-4" data-testid={`admin-property-${p.slug}`}>
            <img src={imgSrc(p.images?.[0])} alt="" className="h-16 w-24 object-cover bg-navy shrink-0" />
            <div className="flex-1 min-w-0">
              <Link to={`/opportunities/${p.slug}`} className="font-semibold text-navy hover:text-gold">{p.title}</Link>
              <p className="text-xs text-mist">{p.property_type} · {p.city} · {fmtMoney(p.asking_price || p.est_acquisition_price) || "Price TBD"} · {p.request_count || 0} requests {p.is_sample && "· SAMPLE"}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => toggleFeatured(p)} title="Featured" data-testid={`admin-feature-${p.slug}`}><Star className={`h-4 w-4 ${p.featured ? "fill-gold text-gold" : "text-mist"}`} /></button>
              <select className="field !py-2 !w-auto text-sm" value={p.status} onChange={(e) => setStatus(p, e.target.value)} data-testid={`admin-status-${p.slug}`}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
              <button onClick={() => setMatches(p)} className="btn-ghost text-navy !py-2 !px-3 text-xs" data-testid={`admin-matches-${p.slug}`}><Users className="h-3.5 w-3.5" /> Matches</button>
              <button onClick={() => setEdit(p)} className="btn-ghost text-navy !py-2 !px-3 text-xs" data-testid={`admin-edit-${p.slug}`}>Edit</button>
            </div>
          </div>
        ))}
      </div>
      {edit && <PropertyEditor open property={edit.id ? edit : null} onClose={() => setEdit(null)} onSaved={load} />}
      {matches && <MatchesDialog p={matches} onClose={() => setMatches(null)} />}
    </div>
  );
}

function Investors() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/admin/investors").then((r) => setUsers(r.data)); }, []);
  return (
    <div className="space-y-8">
      <div>
        <p className="eyebrow mb-3">Investor Accounts ({users.length})</p>
        <div className="border border-navy/10 bg-white divide-y divide-navy/10" data-testid="admin-investor-accounts">
          {users.map((u) => <div key={u.id} className="p-4 text-sm"><span className="font-semibold text-navy">{u.name}</span> <span className="text-mist">· {u.email} · {u.company || "—"} · {u.phone || "—"}</span></div>)}
        </div>
      </div>
      <div><p className="eyebrow mb-3">Investor Criteria & Leads</p><Records kind="criteria" /></div>
    </div>
  );
}

function Notify() {
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({ user_id: "all", title: "", body: "", link: "" });
  useEffect(() => { api.get("/admin/investors").then((r) => setUsers(r.data)); }, []);
  const send = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/notifications", f); toast.success("Notification sent"); setF({ ...f, title: "", body: "" }); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={send} className="max-w-2xl bg-white border border-navy/10 p-6 space-y-4" data-testid="admin-notify-form">
      <Field label="Recipient"><select className="field" value={f.user_id} onChange={(e) => setF({ ...f, user_id: e.target.value })} data-testid="notify-recipient"><option value="all">All investors</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}</select></Field>
      <Field label="Title"><input required className="field" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="notify-title" /></Field>
      <Field label="Message"><textarea required className="field min-h-[100px]" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="notify-body" /></Field>
      <Field label="Link (optional)"><input className="field" placeholder="/opportunities/..." value={f.link} onChange={(e) => setF({ ...f, link: e.target.value })} data-testid="notify-link" /></Field>
      <button className="btn-primary" data-testid="notify-send">Send In-App Notification</button>
      <p className="text-xs text-mist">Email alerts are not enabled in this version — notifications appear in the investor portal.</p>
    </form>
  );
}

function Fees() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/settings/fees").then((r) => setItems(r.data)); }, []);
  const upd = (i, k, v) => setItems(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const save = async () => { try { await api.put("/admin/settings/fees", items); toast.success("Fee schedule saved"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-4 max-w-3xl" data-testid="admin-fees">
      <p className="text-sm text-ink/70">Internal fee configuration. Fees are not displayed publicly. All applicable fees must be disclosed to clients in writing before they commit to a transaction.</p>
      {items.map((x, i) => (
        <div key={x.key} className="bg-white border border-navy/10 p-4 grid gap-3 sm:grid-cols-[1.2fr_1fr_1.5fr_auto] items-center" data-testid={`fee-${x.key}`}>
          <span className="font-semibold text-navy text-sm">{x.label}</span>
          <input className="field !py-2 text-sm" placeholder="e.g. 1% or flat" value={x.value} onChange={(e) => upd(i, "value", e.target.value)} data-testid={`fee-value-${x.key}`} />
          <input className="field !py-2 text-sm" placeholder="Description / terms" value={x.description} onChange={(e) => upd(i, "description", e.target.value)} />
          <label className="flex items-center gap-2 text-xs text-navy"><input type="checkbox" checked={x.enabled} onChange={(e) => upd(i, "enabled", e.target.checked)} data-testid={`fee-enabled-${x.key}`} /> Active</label>
        </div>
      ))}
      <button onClick={save} className="btn-primary" data-testid="fees-save">Save Fee Schedule</button>
    </div>
  );
}

const TABS = [["overview", "Overview", Overview], ["properties", "Properties", Properties], ["investors", "Investors", Investors],
  ["requests", "Info Requests", () => <Records kind="requests" />], ["sellers", "Seller Submissions", () => <Records kind="sellers" />],
  ["inquiries", "Inquiries", () => <Records kind="inquiries" />], ["messages", "Messages", () => <Messages isAdmin />], ["notify", "Notifications", Notify], ["fees", "Fees", Fees]];

export default function Admin() {
  useSeo("Admin");
  const { logout } = useAuth();
  return (
    <section className="bg-paper pt-[110px] pb-24" data-testid="admin-dashboard">
      <div className="container-x">
        <div className="flex items-end justify-between gap-4">
          <div><p className="eyebrow">Sido Holdings · Internal</p><h1 className="mt-3 text-4xl sm:text-5xl font-medium text-navy">Admin Dashboard</h1></div>
          <button onClick={logout} className="inline-flex items-center gap-2 text-sm text-mist hover:text-navy" data-testid="admin-logout"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
        <Tabs defaultValue="overview" className="mt-10">
          <TabsList className="h-auto flex-wrap justify-start gap-1 bg-transparent p-0 border-b border-navy/10 w-full rounded-none">
            {TABS.map(([v, l]) => <TabsTrigger key={v} value={v} className="rounded-none px-4 py-3 text-navy data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-gold" data-testid={`admin-tab-${v}`}>{l}</TabsTrigger>)}
          </TabsList>
          {TABS.map(([v, , C]) => <TabsContent key={v} value={v} className="mt-8"><C /></TabsContent>)}
        </Tabs>
      </div>
    </section>
  );
}
